import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getEquipmentTypeReviews,
  getEquipmentTypeReviewSummary,
  getReservationReviewStatus,
  submitReservationReview,
  updateReservationReview,
} from '@/api/reviews';
import type {
  EquipmentTypeReviewsRequest,
  SubmitReservationReviewRequest,
  UpdateReservationReviewRequest,
} from '@/types/reviews';
import { reservationKeys } from '@/hooks/useReservations';

export const reviewKeys = {
  all: ['reviews'] as const,
  reservationStatus: (reservationId: string) => [...reviewKeys.all, 'reservation-status', reservationId] as const,
  equipmentTypeReviews: (equipmentTypeId: string, sortBy: EquipmentTypeReviewsRequest['sortBy']) =>
    [...reviewKeys.all, 'equipment-type-reviews', equipmentTypeId, sortBy ?? 'newest'] as const,
  equipmentTypeSummary: (equipmentTypeId: string) =>
    [...reviewKeys.all, 'equipment-type-summary', equipmentTypeId] as const,
};

export const useReservationReviewStatus = (reservationId: string, enabled = true) => {
  return useQuery({
    queryKey: reviewKeys.reservationStatus(reservationId),
    queryFn: () => getReservationReviewStatus(reservationId),
    enabled: Boolean(reservationId) && enabled,
    staleTime: 60 * 1000,
  });
};

export const useSubmitReservationReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SubmitReservationReviewRequest) => submitReservationReview(request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.reservationStatus(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.detail(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
    },
  });
};

export const useUpdateReservationReview = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ reservationId, request }: { reservationId: string; request: UpdateReservationReviewRequest }) =>
      updateReservationReview(reservationId, request),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: reviewKeys.reservationStatus(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.detail(variables.reservationId) });
      queryClient.invalidateQueries({ queryKey: reservationKeys.lists() });
    },
  });
};

export const REVIEWS_PAGE_SIZE = 10;

/**
 * Paged equipment-type reviews via TanStack infinite query.
 *
 * The API returns a flat array of reviews for the requested page (no total/hasNext
 * envelope), so we infer the next page from the page size: a full page (=== pageSize)
 * means there may be more; a short/empty page means we've reached the end. The total
 * approved count for the summary header comes from the separate review-summary endpoint.
 */
export const useEquipmentTypeReviews = (
  equipmentTypeId: string | undefined,
  sortBy: EquipmentTypeReviewsRequest['sortBy'] = 'newest',
  pageSize: number = REVIEWS_PAGE_SIZE,
) => {
  return useInfiniteQuery({
    queryKey: reviewKeys.equipmentTypeReviews(equipmentTypeId ?? 'unknown', sortBy),
    queryFn: ({ pageParam }) =>
      getEquipmentTypeReviews(equipmentTypeId!, { sortBy, page: pageParam, pageSize }),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) =>
      lastPage.length === pageSize ? allPages.length + 1 : undefined,
    enabled: Boolean(equipmentTypeId),
    staleTime: 2 * 60 * 1000,
  });
};

export const useEquipmentTypeReviewSummary = (equipmentTypeId: string | undefined) => {
  return useQuery({
    queryKey: reviewKeys.equipmentTypeSummary(equipmentTypeId ?? 'unknown'),
    queryFn: () => getEquipmentTypeReviewSummary(equipmentTypeId!),
    enabled: Boolean(equipmentTypeId),
    staleTime: 2 * 60 * 1000,
  });
};
